// src/features/shopfloor/api.ts
// ADR-102.
//
// ⚠️ TEMPORARY. Once backend/apps/production/shopfloor is merged and
// drf-spectacular's schema is regenerated, run `npx orval` and replace
// every hook below with the generated
// useV1ProductionShopfloor<Resource><Action> hooks (see MrpRunListPage.tsx
// for the naming pattern this project follows). Keeping a thin hand-written
// layer here only so the Phase A/B pages and their tests are runnable
// before that regeneration step — do not build new features on top of this
// file once real generated hooks exist.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance as apiClient } from "@/api/client"; // existing shared axios/fetch instance
import type {
  DowntimeReasonCode,
  DowntimeRecord,
  Shift,
  ShiftAssignment,
  WorkInstruction,
} from "./types";

const BASE = "/api/v1/production/shopfloor";

function extractList<T>(data: { results?: T[] } | T[] | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : data.results ?? [];
}

// -- Shifts ------------------------------------------------------------

export function useShiftsList() {
  return useQuery({
    queryKey: ["shopfloor", "shifts"],
    queryFn: async () => extractList<Shift>((await apiClient.get(`${BASE}/shifts/`)).data),
  });
}

export function useCreateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Omit<Shift, "id">) => apiClient.post<Shift>(`${BASE}/shifts/`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopfloor", "shifts"] }),
  });
}

// -- Shift Assignments ---------------------------------------------------

export function useShiftAssignmentsList(params?: { work_center?: string; date?: string }) {
  return useQuery({
    queryKey: ["shopfloor", "shift-assignments", params],
    queryFn: async () =>
      extractList<ShiftAssignment>((await apiClient.get(`${BASE}/shift-assignments/`, { params })).data),
  });
}

export function useCreateShiftAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Omit<ShiftAssignment, "id" | "shift_display" | "work_center_display">) =>
      apiClient.post<ShiftAssignment>(`${BASE}/shift-assignments/`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopfloor", "shift-assignments"] }),
  });
}

// -- Work Instructions -----------------------------------------------------

export function useWorkInstructionsList(routingOperationId?: string) {
  return useQuery({
    queryKey: ["shopfloor", "work-instructions", routingOperationId],
    queryFn: async () =>
      extractList<WorkInstruction>(
        (await apiClient.get(`${BASE}/work-instructions/`, {
          params: routingOperationId ? { routing_operation: routingOperationId } : undefined,
        })).data,
      ),
  });
}

// -- Downtime ----------------------------------------------------------

export function useDowntimeReasonCodesList() {
  return useQuery({
    queryKey: ["shopfloor", "downtime-reason-codes"],
    queryFn: async () =>
      extractList<DowntimeReasonCode>((await apiClient.get(`${BASE}/downtime-reason-codes/`)).data),
  });
}

export function useDowntimeRecordsList(workCenterId?: string) {
  return useQuery({
    queryKey: ["shopfloor", "downtime-records", workCenterId],
    queryFn: async () =>
      extractList<DowntimeRecord>(
        (await apiClient.get(`${BASE}/downtime-records/`, {
          params: workCenterId ? { work_center: workCenterId } : undefined,
        })).data,
      ),
  });
}

export function useStartDowntime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { work_center: string; reason_code: string; machine?: string; related_lot?: string; notes?: string }) =>
      apiClient.post<DowntimeRecord>(`${BASE}/downtime-records/start/`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopfloor", "downtime-records"] }),
  });
}

export function useEndDowntime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (downtimeRecordId: string) =>
      apiClient.post<DowntimeRecord>(`${BASE}/downtime-records/${downtimeRecordId}/end/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shopfloor", "downtime-records"] }),
  });
}
